import styled from '@emotion/styled';
import { FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import { createElement, Dispatch, FC, Fragment, SetStateAction, SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMount } from 'react-use';

import { UploadingAssetFilesProgress } from '../../../../entity/UploadingAssetFilesProgress';
import {
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionSelectOne,
  DynamicSchemaDefinitionSingleLineText,
} from '../../../../generated/graphql';
import { EnhancedProjectFileList } from '../../../connected/EnhancedProjectFileList';
import { SPACING_REGULAR } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';
import { AnyFieldValue, FileIdsValue, ProjectAssetEditorFieldValues } from '../../../types/DynamicSchema';
import { Meta } from '../../../types/Meta';

type Props = {
  className?: string;
  field: DynamicSchemaDefinitionField;
  value: ProjectAssetEditorFieldValues;
  bucketId: string;
  meta: Meta;
  onChange: (e: SyntheticEvent<HTMLElement>, value: AnyFieldValue) => void;
  enableCategory: boolean;
  toggleEnableCategory: Dispatch<SetStateAction<boolean>>;
  filesProgress: UploadingAssetFilesProgress['categoryKey'];
};

const Field: FC<Props> = ({
  field,
  meta,
  value,
  bucketId,
  onChange,
  enableCategory,
  toggleEnableCategory,
  filesProgress,
}) => {
  useMount(() => {
    if (meta.isAllowLeaveToTheCreators && field.type === 'SELECT_ONE' && value === 'unnecessary') {
      toggleEnableCategory(false);
    }
  });

  const onChangeAllowLeaveToTheCreators = (e: SyntheticEvent<HTMLElement, Event>, value: string) => {
    if (value === 'unnecessary') {
      toggleEnableCategory(false);
    } else {
      toggleEnableCategory(true);
    }
    onChange(e, value);
  };

  if (meta.isAllowLeaveToTheCreators && field.type === 'SELECT_ONE') {
    return (
      <>
        <Typography>
          <FormattedMessage id="project_basic_setting_detail.assets.label.leave_to_the_creators.form_description" />
        </Typography>
        <SelectLeaveToTheCreators>
          <RadioGroup value={value} onChange={onChangeAllowLeaveToTheCreators}>
            {(field as DynamicSchemaDefinitionSelectOne).choices.map((choice) => (
              <FormControlLabel
                disabled={Object.keys(filesProgress).length > 0}
                control={<Radio />}
                value={choice.key}
                label={<Typography variant="body2">{choice.label}</Typography>}
                key={choice.key}
              />
            ))}
          </RadioGroup>
        </SelectLeaveToTheCreators>
      </>
    );
  }

  if (field.type === 'FILE_IDS') {
    return (
      <EnhancedProjectFileList
        meta={meta}
        fieldKey={field.key}
        fileIds={value as FileIdsValue}
        bucketId={bucketId}
        disabled={!enableCategory}
      />
    );
  }

  if (field.type === 'SINGLE_LINE_TEXT') {
    return (
      <SharedUrlField
        label={<FormattedMessage id="project_editing_page.creative_brief.asset_form.url.label" />}
        variant="outlined"
        disabled={!enableCategory}
        onChange={(e) => onChange(e, e.currentTarget.value)}
        defaultValue={value}
        InputLabelProps={{
          shrink: true,
        }}
        placeholder={(field as DynamicSchemaDefinitionSingleLineText).placeholder}
      />
    );
  }

  if (field.type === 'MULTI_LINE_TEXT') {
    return (
      <DescriptionField
        disabled={!enableCategory}
        multiline
        rows={2}
        label={<FormattedMessage id="project_editing_page.creative_brief.asset_form.option.label" />}
        variant="outlined"
        onChange={(e) => onChange(e, e.currentTarget.value)}
        defaultValue={value}
        InputLabelProps={{
          shrink: true,
        }}
        placeholder={(field as DynamicSchemaDefinitionMultiLineText).placeholder}
      />
    );
  }

  return null;
};

const SelectLeaveToTheCreators = styled(Box)`
  background-color: #fafafa;
  padding: ${SPACING_REGULAR}px;
  margin-bottom: ${SPACING_REGULAR}px;

  .MuiFormGroup-root {
    flex-direction: row;
  }
`;

const SharedUrlField = styled(TextField)<{ disabled: boolean }>`
  padding-bottom: ${SPACING_REGULAR}px;
  ${({ disabled }) => disabled && 'opacity: 0.8;'};
  ${({ disabled }) => disabled && 'background: #e8e8e8;'};

  ${mq('sp')} {
    .MuiOutlinedInput-root {
      input::placeholder {
        font-size: 10px;
      }
    }
  }
`;

const DescriptionField = styled(TextField)<{ disabled: boolean }>`
  ${({ disabled }) => disabled && 'opacity: 0.8;'};
  ${({ disabled }) => disabled && 'background: #e8e8e8;'};
`;

export default Field;
